import * as df from 'date-fns';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import scrollToTop from '../../../hoc/scrollToTop';

import Calendar from './InputBookingDateTimeCalendar';
import Marubatsu from './InputBookingDateTimeMarubatsu';
import Preview from './InputBookingTargetPreview';

import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import * as Actions from '../../../../redux/actions/merchants/resources/books';
import {
  AvailableDateTimeQuery,
  CalendarQuery,
} from '../../../../redux/actions/merchants/resources/books/api';
import * as Inputs from '../../../../redux/actions/merchants/resources/books/inputs';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingBookingDateTimes;
  postable: boolean;
  backToModifyPage: () => void;
  onSubmit: () => void;
  onRestart: () => void;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    clearCalendar: () => void;
    setCalendar: (q: CalendarQuery) => Promise<void>;
    setAvailableDates: (q: AvailableDateTimeQuery) => Promise<void>;
    setAvailableTimes: (q: AvailableDateTimeQuery) => Promise<void>;
    handleSubmit: (
      reservationId: number,
      availability: IBooks.TimeSlotAvailability,
    ) => Promise<void>;
    handleClearTargets: (targets: {
      course: boolean;
      options: boolean;
      staff?: boolean;
    }) => void;
  };
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

interface State {
  loading: boolean;
  dates: Date[];
  baseDate: Date;
}

const DISPLAY_DATES = 6;

class InputBookingDateTime extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { selectedDate } = this.props.settings;
    const baseDate =
      selectedDate && df.isValid(df.parse(selectedDate))
        ? df.parse(selectedDate)
        : new Date();
    this.state = {
      loading: false,
      dates: [],
      baseDate,
    };
    this.handleClickToday = this.handleClickToday.bind(this);
    this.handleClickNext = this.handleClickNext.bind(this);
    this.handleClickPrev = this.handleClickPrev.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChangeTarget = this.handleChangeTarget.bind(this);
    this.handleMonthlyChangeDate = this.handleMonthlyChangeDate.bind(this);
    this.handleMonthlyChangeTime = this.handleMonthlyChangeTime.bind(this);
  }

  public componentDidMount() {
    this.props.actions.clearCalendar();
    const { candidateViewType } = this.props.settings;
    if (candidateViewType === 'board') {
      this._setCalendarFrom(this.state.baseDate);
    }
  }

  private async _setCalendar(dates: Date[]) {
    const { actions, inputs } = this.props;
    const { merchantId, resourceId, course, options, staff } = inputs;

    if (!course || !options || !resourceId || !merchantId) {
      return;
    }

    this.setState({ loading: true, dates });
    await actions.setCalendar({
      course,
      end: dates[dates.length - 1],
      options,
      merchantId,
      resourceId,
      staff,
      start: dates[0],
    });
    this.setState({ loading: false, dates });
  }

  private async _setMonthlyCalendarDate(date: Date) {
    const {
      actions,
      inputs: { merchantId, resourceId, course, options, staff },
    } = this.props;
    if (!course || !options || !resourceId || !merchantId) {
      return;
    }
    this.setState({ loading: true, baseDate: date });
    await actions.setAvailableDates({
      merchantId,
      resourceId,
      course,
      options,
      staff,
      date,
    });
    this.setState({ loading: false, baseDate: date });
  }

  private handleMonthlyChangeDate(date: Date) {
    this._setMonthlyCalendarDate(date);
  }

  private async _setMonthlyCalendarTime(date: Date) {
    const {
      actions,
      inputs: { merchantId, resourceId, course, options, staff },
    } = this.props;
    if (!course || !options || !resourceId || !merchantId) {
      return;
    }
    this.setState({ loading: true });
    await actions.setAvailableTimes({
      merchantId,
      resourceId,
      course,
      options,
      staff,
      date,
    });
    this.setState({ loading: false });
  }

  private handleMonthlyChangeTime(date: Date) {
    this._setMonthlyCalendarTime(date);
  }

  private _setCalendarFrom(date: Date) {
    const dates = df.eachDay(date, df.addDays(date, DISPLAY_DATES));
    this._setCalendar(dates);
  }

  private handleClickToday() {
    const today = new Date();
    this._setCalendarFrom(today);
  }

  private handleClickNext() {
    const { dates } = this.state;
    this._setCalendarFrom(df.addDays(dates[0], DISPLAY_DATES + 1));
  }

  private handleClickPrev() {
    const { dates } = this.state;
    const assumedPreviousDate = df.subDays(dates[0], DISPLAY_DATES + 1);
    const today = new Date();
    this._setCalendarFrom(
      df.isBefore(assumedPreviousDate, today) ? today : assumedPreviousDate,
    );
  }

  private async handleSubmit(
    timeSlotAvailability: IBooks.TimeSlotAvailability,
  ) {
    const { inputs, actions, onSubmit, onRestart } = this.props;
    try {
      await actions.handleSubmit(inputs.reservationId!, timeSlotAvailability);
      onSubmit();
    } catch (e) {
      if (e instanceof Inputs.RestartReservationError) {
        onRestart();
      }
    }
  }

  private handleChangeTarget(targets: {
    course: boolean;
    options: boolean;
    staff?: boolean;
  }) {
    this.props.actions.handleClearTargets(targets);
    this.props.backToModifyPage();
  }

  public render() {
    const {
      inputs: { course, options, staff },
      settings: {
        firstSelected,
        events,
        availableDates,
        availableTimes,
        candidateViewType,
        selectedDate,
      },
      postable,
    } = this.props;
    const { loading, dates, baseDate } = this.state;

    if (!course || !options) {
      return null;
    }

    return (
      <div>
        <Preview
          firstSelected={firstSelected}
          course={course}
          options={options}
          staff={staff}
          onChangeTarget={this.handleChangeTarget}
        />
        {candidateViewType === 'board' ? (
          <Marubatsu
            loading={loading}
            displayDates={dates}
            events={events}
            postable={postable}
            onSubmit={this.handleSubmit}
            onClickToday={this.handleClickToday}
            onClickPrev={this.handleClickPrev}
            onClickNext={this.handleClickNext}
          />
        ) : (
          <Calendar
            baseDate={baseDate}
            availableDates={availableDates}
            availableTimes={availableTimes}
            postable={postable}
            onSubmit={this.handleSubmit}
            onChangeBaseDate={this.handleMonthlyChangeDate}
            onChangeSelectedDate={this.handleMonthlyChangeTime}
            selectedDate={selectedDate}
          />
        )}
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleClearTargets: (targets: {
      course: boolean;
      options: boolean;
      staff?: boolean;
    }) => Inputs.clearBookingTargets(targets)(dispatch),
    handleSubmit: (
      reservationId: number,
      availability: IInputs.TimeSlotAvailability,
    ) => Inputs.inputBookingDateTime(reservationId, availability)(dispatch),
    clearCalendar: () => Actions.clearCalendar()(dispatch),
    setCalendar: (q: CalendarQuery) => Actions.setCalendar(q)(dispatch),
    setAvailableDates: (q: AvailableDateTimeQuery) =>
      Actions.setAvailableDates(q)(dispatch),
    setAvailableTimes: (q: AvailableDateTimeQuery) =>
      Actions.setAvailableTimes(q)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputBookingDateTime)));
